// What a key does to the review, without the DOM: left and right step, up and
// down jump to the previous and the next mistake, or to either end when no
// mistake lies that way (`outerBack`/`outerForward`, the outer buttons'
// actions, 2026-10-13), Escape returns from the reader's line. While the
// reader is on a line, right and down do nothing; within a Controls range (a
// puzzle) the steps look along the path and Escape goes back to the board's
// own position. The keydown listener in `index.tsx` calls the method named.
import { isExploring, type Review, type ReviewState } from '@chess/api-review';

/** The review method a key calls, or an outer step the panel resolves (controls.ts). */
export type StepAction = keyof Pick<Review, 'stepBackward' | 'stepForward' | 'toStart' | 'toEnd' | 'returnToGame'> | 'outerBack' | 'outerForward' | 'puzzle' | 'mainLine';

export interface KeyMods {
  meta?: boolean;
  ctrl?: boolean;
  alt?: boolean;
  /** Another handler already took the key. */
  defaultPrevented?: boolean;
}

/** What is focused when the key is pressed. */
export interface KeyTarget {
  tagName?: string;
  isContentEditable?: boolean;
}

/** Keys pressed while typing belong to the field, not to the board. */
export function isTyping(target: KeyTarget | null | undefined): boolean {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!target.isContentEditable;
}

/** The review method a key calls, or undefined when the key is not the steps' (and is left alone). */
export function keyAction(key: string, mods: KeyMods, typing: boolean, state: ReviewState, ranged: boolean): StepAction | undefined {
  if (mods.defaultPrevented || mods.meta || mods.ctrl || mods.alt || typing) return undefined;
  // Within a range the steps look along the path; Escape returns to the board's own position.
  if (ranged && key === 'Escape') return 'toEnd';
  const line = isExploring(state) && !ranged;
  switch (key) {
    case 'ArrowLeft':
      return 'stepBackward';
    case 'ArrowRight':
      return line ? undefined : 'stepForward';
    case 'ArrowUp':
      // On a line, start drops it; in a range, start is the range's; in the game, the previous mistake or the start.
      return line || ranged ? 'toStart' : 'outerBack';
    case 'ArrowDown':
      return line ? undefined : ranged ? 'toEnd' : 'outerForward';
    case 'Escape':
      return line ? 'returnToGame' : undefined;
    // The mistake's actions (2026-10-14); the panel acts only when the bar shows them.
    case 'p':
    case 'P':
      return 'puzzle';
    case 'l':
    case 'L':
      return 'mainLine';
    default:
      return undefined;
  }
}
